import {
    TABLETOP_DEFAULT_BEVEL_SEGMENTS,
    TABLETOP_DEFAULT_BEVEL_SIZE,
} from "../../../../constants";
import {
    BufferAttribute,
    BufferGeometry,
    Euler,
    ExtrudeGeometry,
    Mesh,
    MeshStandardMaterial,
    Vector2,
    Vector3,
} from "three";
import {TSideType} from "../../../../../../common-code/types/TSideType";
import {TPoint2D} from "../../../../../../common-code/types/TPoint2D";
import {TLine} from "../../../../../../common-code/types/TLine";
import {
    KDETAIL_PRICE_TYPE_COLLECTIONS, SIDE_TYPE_BACK, SIDE_TYPE_FRONT,
    SIDE_TYPE_LEFT,
    SIDE_TYPE_RIGHT, TABLETOP_PLANK_TYPE_EURO_ZAPIL,
} from "../../../../../../common-code/constants";
import {ThreeKUnitDetail} from "../ThreeKUnitDetail/ThreeKUnitDetail";
import {ICoverMainPoints} from "../../../../interfaces/ICoverMainPoints";
import {IThreeKUnitDetail} from "../../../../interfaces/IThreeKUnitDetail";
import {ThreeUnit} from "../../ThreeUnit/ThreeUnit";
import {TPositionSideType} from "../../../../../../common-code/types/TPositionSideType";
import {ITabletopData} from "../../../../../../common-code/interfaces/materials/ITabletopData";
import {ISaveKUnitDetailData} from "../../../../../../common-code/interfaces/saveData/ISaveKUnitDetailData";
import {CommonHelper} from "common-code";
import {MathHelper} from "common-code";
import {IMaterialTextures} from "../../../../interfaces/IMaterialTextures";
import {ITextureData} from "../../../../../../common-code/interfaces/materials/ITextureData";
import {i18n} from '../../../../../i18n';
import {ITabletopPlank} from '../../../../../../common-code/interfaces/plank/ITabletopPlank';
import {IEuroZapilPlanks} from '../../../../../../common-code/interfaces/plank/IEuroZapilPlanks';
import {IEuroZapilPlank} from '../../../../../../common-code/interfaces/plank/IEuroZapilPlank';
import {TPoint3D} from '../../../../../../common-code/types/TPoint3D';
import {ExtrudeGeometryOptions} from 'three/src/geometries/ExtrudeGeometry';
import {InterleavedBufferAttribute} from 'three/src/core/InterleavedBufferAttribute';
import {GLBufferAttribute} from 'three/src/core/GLBufferAttribute';
import {TSizes} from '../../../../../../common-code/types/geometry/TSizes';

export class ThreeTabletop extends ThreeKUnitDetail implements IThreeKUnitDetail {
    materialData: ITabletopData;
    intervalPointA: Vector3;
    intervalPointB: Vector3;

    constructor(options: ISaveKUnitDetailData, unit: ThreeUnit) {
        super(options, unit);
        this.materialData = this.initMaterialData();
        this.intervalPointA = new Vector3();
        this.intervalPointB = new Vector3();
        if (
            this.saveData.sizes &&
            this.saveData.sizes.height &&
            this.materialData.height !== this.saveData.sizes.height
        ) {
            this.saveData.sizes.height = +this.materialData.height;
        }
    }

    public tryRebuildBody() {
        let shapePoints: Vector2[];

        shapePoints = this.calculateShapePoints();
        if (this.shapePoints &&
            !CommonHelper.deepCompare(shapePoints, this.shapePoints)) {
            this.createShape(shapePoints);
            this.rebuildBody();
        }
    }

    public getGlobalMainPoints(cover: Mesh = this.cover): ICoverMainPoints {
        if (!this.correctLeftPoints || !this.correctRightPoints) {
            throw new Error('error-ThreeTabletop-getGlobalMainPoints');
        }
        this.view3d.updateMatrixWorld();
        this.globalCoverMainPoints.back.pointA.set(
            this.correctLeftPoints[0].x,
            0,
            this.correctLeftPoints[0].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.back.pointB.set(
            this.correctRightPoints[0].x,
            0,
            this.correctRightPoints[0].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.front.pointA.set(
            this.correctLeftPoints[this.correctLeftPoints.length - 1].x,
            0,
            this.correctLeftPoints[this.correctLeftPoints.length - 1].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.front.pointB.set(
            this.correctRightPoints[this.correctRightPoints.length - 1].x,
            0,
            this.correctRightPoints[this.correctRightPoints.length - 1].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.left.pointA.set(
            this.correctLeftPoints[0].x,
            0,
            this.correctLeftPoints[0].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.left.pointB.set(
            this.correctLeftPoints[this.correctLeftPoints.length - 1].x,
            0,
            this.correctLeftPoints[this.correctLeftPoints.length - 1].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.right.pointA.set(
            this.correctRightPoints[0].x,
            0,
            this.correctRightPoints[0].y
        ).applyMatrix4(this.view3d.matrixWorld);
        this.globalCoverMainPoints.right.pointB.set(
            this.correctRightPoints[this.correctRightPoints.length - 1].x,
            0,
            this.correctRightPoints[this.correctRightPoints.length - 1].y
        ).applyMatrix4(this.view3d.matrixWorld);

        return this.globalCoverMainPoints;
    }

    public getDefaultPositionByType(): Vector3 {
        return this.unit.getInitTabletopPosition(this);
    }

    public getDefaultPoints(type: TSideType): Vector2[] {
        let points: Vector2[];

        points = [
            new Vector2(0, -this.getWidth() / 2),
            new Vector2(0, this.getWidth() / 2),
        ];

        return points;
    }

    public initHeight(positionType?: TPositionSideType): number {
        return this.service.getTabletopHeight();
    }

    public initLength(positionType?: TPositionSideType): number {
        return this.unit.getWidth();
    }

    public getMaterialData(): ITabletopData {
        return this.materialData;
    }

    public getSettingName(): string {
        switch (this.getPositionType()) {
            case SIDE_TYPE_BACK:
            default:
                return i18n.t('Коллекция')
        }
    }

    public getVisibleTitle(): string {
        let title: string;

        title = i18n.t('Строить столешницу');
        if (this.saveData.isSelfVisible !== undefined) {
            title += ' (' + i18n.t('вручную') + ')';
        } else {
            title += ' (' + i18n.t('автоматически') + ')';
        }

        return title;
    }

    /**
     * Вычисляем ширину столешницы по-умолчанию
     * @protected
     */
    public initWidth(_positionType?: TPositionSideType): number {
        if (this.unit.getDepth() < this.service.getBottomUnitDepth(this.getTechnologMapFacadeId())) {
            return this.unit.getDepth() + this.service.getTabletopFrontGap(this.getTechnologMapFacadeId())
        }
       return this.service.getTabletopWidth();
    }

    public getUnionYPosition(): number {
        return -this.getHeightShape() / 2;
    }

    public getMaterialId(): string {
        return this.materialData.id;
    }

    public getTechnologMapCuttingGap(): number {
        return this.service.getTabletopCuttingGap(this.getTechnologMapFacadeId());
    }

    public getRealLength(): number {
        let euroZapilPlanks: IEuroZapilPlanks;
        let length: number = this.getLength();

        euroZapilPlanks = this.getEuroZapilPlanks();
        if (euroZapilPlanks[SIDE_TYPE_FRONT]) {
            length += 50;
        }
        if (euroZapilPlanks[SIDE_TYPE_LEFT]) {
            length += 100;
        }
        if (euroZapilPlanks[SIDE_TYPE_RIGHT]) {
            length += 100;
        }

        return length;
    }

    public getRedLines(): TLine[] {
        let redLines: TLine[] = [];
        let sideId: string;
        let euroZapilPlank: IEuroZapilPlank;
        const euroZapilPlanks: IEuroZapilPlanks = this.getEuroZapilPlanks();

        for (sideId in euroZapilPlanks) {
            for (euroZapilPlank of euroZapilPlanks[sideId]) {
                redLines.push({
                    pointA: {x: euroZapilPlank.localInterval.pointA.x, y: euroZapilPlank.localInterval.pointA.z},
                    pointB: {x: euroZapilPlank.localInterval.pointB.x, y: euroZapilPlank.localInterval.pointB.z},
                })
            }
        }
        return redLines;
    }

    protected getOtherDetails(): ThreeKUnitDetail[] {
        return this.unit.tabletops || [];
    }

    protected loadTextures(): IMaterialTextures {
        let textures: ITextureData[];
        switch (this.materialData.type) {
            case KDETAIL_PRICE_TYPE_COLLECTIONS:
                if (this.materialData.offerTextures) {
                    textures = Object.values(this.materialData.offerTextures)[0] || [];
                } else {
                    textures = this.materialData.textures || [];
                }
                break;
            default:
                textures = this.materialData.textures || [];
                break;
        }

        return this.service.loadMaterialTextures(this.materialData.id, textures);
    }

    protected initMaterialData(): ITabletopData {
        let material: ITabletopData;
        let height: number | undefined;

        height =
            this.saveData.sizes && this.saveData.sizes.height
                ? this.saveData.sizes.height
                : undefined;
        material = CommonHelper.deepCopy(
            this.service.getTabletopMaterial(this.saveData.material)
        );
        if (height && material.heights && material.heights.includes(height)) {
            material.height = height;
        }

        return material;
    }

    protected initPriceType() {
        if (
            this.saveData.priceType === undefined &&
            this.service.appConfig.catalog.tabletops !== undefined
        ) {
            this.saveData.priceType =
                this.service.appConfig.catalog.tabletops.priceType;
        }
    }

    protected getEuroZapilPlanks(): IEuroZapilPlanks {
        let index: string;
        let tabletopPlank: ITabletopPlank;
        let sideId: TSideType | undefined;
        let neighborSideId: TSideType | undefined;
        let euroZapilPlanks: IEuroZapilPlanks = {};
        let pointA: TPoint3D;
        let pointB: TPoint3D;


        if (!this.service.isEuroZapil()) {
            return euroZapilPlanks;
        }
        const tabletopPlanks: ITabletopPlank[] = this.service.getTabletopPlanksByFunctionalType(TABLETOP_PLANK_TYPE_EURO_ZAPIL);

        for (tabletopPlank of tabletopPlanks) {
            if (tabletopPlank.functionalType === TABLETOP_PLANK_TYPE_EURO_ZAPIL) {
                sideId = undefined;
                neighborSideId = undefined;
                if (tabletopPlank.tabletop1 === this.getId()) {
                    sideId = tabletopPlank.side1;
                    neighborSideId = tabletopPlank.side2;
                }
                if (tabletopPlank.tabletop2 === this.getId()) {
                    sideId = tabletopPlank.side2;
                    neighborSideId = tabletopPlank.side1;
                }
                if (sideId && neighborSideId) {
                    if (!euroZapilPlanks[sideId]) {
                        euroZapilPlanks[sideId] = [];
                    }
                    this.intervalPointA.copy(this.view3d.worldToLocal(
                        this.intervalPointA.set(
                            tabletopPlank.interval.pointA.x,
                            tabletopPlank.interval.pointA.y,
                            tabletopPlank.interval.pointA.z
                        )
                    ).round());
                    this.intervalPointB.copy(this.view3d.worldToLocal(
                        this.intervalPointB.set(
                            tabletopPlank.interval.pointB.x,
                            tabletopPlank.interval.pointB.y,
                            tabletopPlank.interval.pointB.z
                        )
                    ).round());
                    pointA = {x: +this.intervalPointA.x, y: +this.intervalPointA.y, z: +this.intervalPointA.z};
                    pointB = {x: +this.intervalPointB.x, y: +this.intervalPointB.y, z: +this.intervalPointB.z};
                    switch (sideId) {
                        case SIDE_TYPE_LEFT:
                            if (pointA.z > pointB.z) {
                                pointB = {x: +this.intervalPointA.x, y: +this.intervalPointA.y, z: +this.intervalPointA.z};
                                pointA = {x: +this.intervalPointB.x, y: +this.intervalPointB.y, z: +this.intervalPointB.z};
                            }
                            break;
                        case SIDE_TYPE_RIGHT:
                            if (pointA.z < pointB.z) {
                                pointB = {x: +this.intervalPointA.x, y: +this.intervalPointA.y, z: +this.intervalPointA.z};
                                pointA = {x: +this.intervalPointB.x, y: +this.intervalPointB.y, z: +this.intervalPointB.z};
                            }
                            break;
                        case SIDE_TYPE_FRONT:
                            if (pointA.x > pointB.x) {
                                pointB = {x: +this.intervalPointA.x, y: +this.intervalPointA.y, z: +this.intervalPointA.z};
                                pointA = {x: +this.intervalPointB.x, y: +this.intervalPointB.y, z: +this.intervalPointB.z};
                            }
                            break;
                    }
                    euroZapilPlanks[sideId].push({
                        ...tabletopPlank,
                        localInterval: {
                            pointA: pointA,
                            pointB: pointB
                        },
                        neighborSide: neighborSideId
                    });
                }
            }
        }
        for (index in euroZapilPlanks) {
            switch (index) {
                case SIDE_TYPE_LEFT:
                    euroZapilPlanks[index].sort(function (a: IEuroZapilPlank, b: IEuroZapilPlank) {
                        return a.localInterval.pointA.z - b.localInterval.pointA.z;
                    });
                    break;
                case SIDE_TYPE_RIGHT:
                    euroZapilPlanks[index].sort(function (a: IEuroZapilPlank, b: IEuroZapilPlank) {
                        return b.localInterval.pointA.z - a.localInterval.pointA.z;
                    });
                    break
                case SIDE_TYPE_FRONT:
                default:
                    euroZapilPlanks[index].sort(function (a: IEuroZapilPlank, b: IEuroZapilPlank) {
                        return a.localInterval.pointA.x - b.localInterval.pointA.x;
                    });
                    break;
            }
        }

        return euroZapilPlanks;
    }

    protected calculateShapePoints(): Vector2[] {
        let shapePoints: Vector2[] = [];
        let uniquePoints: { [key: string]: boolean } = {};
        let index: number;
        let point: TPoint2D;
        let firstPoint: TPoint2D;
        let prevPoint: Vector2 | TPoint2D;
        let nextPoint: Vector2 | TPoint2D;
        let euroZapilPlanks: IEuroZapilPlanks;

        if (!this.correctLeftPoints || !this.correctRightPoints) {
            throw new Error('error-ThreeTabletop-calculateShapePoints');
        }
        euroZapilPlanks = this.getEuroZapilPlanks();
        prevPoint = (this.correctRightPoints[0].x.toFixed(3) === this.correctLeftPoints[0].x.toFixed(3) &&
            this.correctRightPoints[0].y.toFixed(3) === this.correctLeftPoints[0].y.toFixed(3)) ?
            this.correctRightPoints[1] : this.correctRightPoints[0];
        firstPoint = this.getPointWithBevel(
            prevPoint, this.correctLeftPoints[0], this.correctLeftPoints[1], SIDE_TYPE_LEFT
        );
        shapePoints.push(new Vector2(firstPoint.x, firstPoint.y))
        uniquePoints[firstPoint.x + '_' + firstPoint.y] = true;
        for (index = 1; index < this.correctLeftPoints.length - 1; index++) {
            this.addEuroZapilLeftPoints(
                euroZapilPlanks[SIDE_TYPE_LEFT],
                this.correctLeftPoints[index - 1],
                this.correctLeftPoints[index],
                shapePoints,
                uniquePoints
            );
            point = this.getPointWithBevel(
                this.correctLeftPoints[index - 1],
                this.correctLeftPoints[index],
                this.correctLeftPoints[index + 1],
                SIDE_TYPE_LEFT
            );
            if (!uniquePoints[point.x + '_' + point.y]) {
                shapePoints.push(new Vector2(point.x, point.y))
                uniquePoints[point.x + '_' + point.y] = true;
            }
        }
        if (this.correctLeftPoints.length > 1) {
            this.addEuroZapilLeftPoints(
                euroZapilPlanks[SIDE_TYPE_LEFT],
                this.correctLeftPoints[this.correctLeftPoints.length - 2],
                this.correctLeftPoints[this.correctLeftPoints.length - 1],
                shapePoints,
                uniquePoints
            );
            nextPoint = (this.correctRightPoints[this.correctRightPoints.length - 1].x.toFixed(3) === this.correctLeftPoints[0].x.toFixed(3) &&
                this.correctRightPoints[this.correctRightPoints.length - 1].y.toFixed(3) === this.correctLeftPoints[0].y.toFixed(3)) ?
                this.correctRightPoints[this.correctRightPoints.length - 2] : this.correctRightPoints[this.correctRightPoints.length - 1];
            point = this.getPointWithBevel(
                this.correctLeftPoints[this.correctLeftPoints.length - 2],
                this.correctLeftPoints[this.correctLeftPoints.length - 1],
                nextPoint,
                SIDE_TYPE_LEFT
            );
            if (!uniquePoints[point.x + '_' + point.y]) {
                shapePoints.push(new Vector2(point.x, point.y))
                uniquePoints[point.x + '_' + point.y] = true;
            }
        }
        this.addEuroZapilFrontPoints(
            euroZapilPlanks[SIDE_TYPE_FRONT],
            this.correctLeftPoints[this.correctLeftPoints.length - 1],
            this.correctRightPoints[this.correctRightPoints.length - 1],
            shapePoints,
            uniquePoints
        );
        prevPoint = (this.correctRightPoints[this.correctRightPoints.length - 1].x.toFixed(3) === this.correctLeftPoints[this.correctLeftPoints.length - 1].x.toFixed(3) &&
            this.correctRightPoints[this.correctRightPoints.length - 1].y.toFixed(3) === this.correctLeftPoints[this.correctLeftPoints.length - 1].y.toFixed(3)) ?
            this.correctLeftPoints[this.correctLeftPoints.length - 2] : this.correctLeftPoints[this.correctLeftPoints.length - 1];
        point = this.getPointWithBevel(
            prevPoint,
            this.correctRightPoints[this.correctRightPoints.length - 1],
            this.correctRightPoints[this.correctRightPoints.length - 2],
            SIDE_TYPE_RIGHT
        );
        if (!uniquePoints[point.x + '_' + point.y]) {
            shapePoints.push(new Vector2(point.x, point.y));
            uniquePoints[point.x + '_' + point.y] = true;
        }
        for (index = this.correctRightPoints.length - 2; index > 0; index--) {
            this.addEuroZapilRightPoints(
                euroZapilPlanks[SIDE_TYPE_RIGHT],
                this.correctRightPoints[index + 1],
                this.correctRightPoints[index],
                shapePoints,
                uniquePoints
            );
            point = this.getPointWithBevel(
                this.correctRightPoints[index + 1],
                this.correctRightPoints[index],
                this.correctRightPoints[index - 1],
                SIDE_TYPE_RIGHT
            );
            if (!uniquePoints[point.x + '_' + point.y]) {
                shapePoints.push(new Vector2(point.x, point.y));
                uniquePoints[point.x + '_' + point.y] = true;
            }
        }
        if (this.correctRightPoints.length > 1) {
            this.addEuroZapilRightPoints(
                euroZapilPlanks[SIDE_TYPE_RIGHT],
                this.correctLeftPoints[1],
                this.correctRightPoints[0],
                shapePoints,
                uniquePoints
            );
            nextPoint = (this.correctRightPoints[0].x.toFixed(3) === this.correctLeftPoints[0].x.toFixed(3) &&
                this.correctRightPoints[0].y.toFixed(3) === this.correctLeftPoints[0].y.toFixed(3)) ?
                this.correctLeftPoints[1] : this.correctLeftPoints[0];
            point = this.getPointWithBevel(
                this.correctRightPoints[1],
                this.correctRightPoints[0],
                nextPoint,
                SIDE_TYPE_RIGHT
            );
            if (!uniquePoints[point.x + '_' + point.y]) {
                shapePoints.push(new Vector2(point.x, point.y));
                uniquePoints[point.x + '_' + point.y] = true;
            }
        }
        shapePoints.push(new Vector2(firstPoint.x, firstPoint.y));

        return shapePoints;
    }

    protected addEuroZapilLeftPoints(
        euroZapilPlanks: IEuroZapilPlank[] | undefined,
        startPoint: Vector2 | TPoint2D,
        endPoint: Vector2 | TPoint2D,
        shapePoints: Vector2[],
        uniquePoints: { [key: string]: boolean }
        ) {
        let euroZapilPlank: IEuroZapilPlank;
        let points: TPoint2D[];
        let point: TPoint2D;

        if (!euroZapilPlanks || MathHelper.isEqualPoints2D(startPoint, endPoint)) {
            return;
        }
        for (euroZapilPlank of euroZapilPlanks) {
            if (euroZapilPlank.localInterval.pointA.z >= startPoint.y &&
                euroZapilPlank.localInterval.pointB.z <= endPoint.y) {
                points = [
                    {
                        x: euroZapilPlank.localInterval.pointA.x + TABLETOP_DEFAULT_BEVEL_SIZE,
                        y: euroZapilPlank.localInterval.pointA.z +
                            (euroZapilPlank.localInterval.pointA.z === startPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    },
                    {
                        x: euroZapilPlank.localInterval.pointA.x + TABLETOP_DEFAULT_BEVEL_SIZE - this.service.getEuroZapilDepth(),
                        y: euroZapilPlank.localInterval.pointA.z +
                            (euroZapilPlank.localInterval.pointA.z === startPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    },
                    {
                        x: euroZapilPlank.localInterval.pointB.x + TABLETOP_DEFAULT_BEVEL_SIZE - this.service.getEuroZapilDepth(),
                        y: euroZapilPlank.localInterval.pointB.z - this.service.getEuroZapilDepth() -
                            (euroZapilPlank.localInterval.pointB.z === endPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    },
                    {
                        x: euroZapilPlank.localInterval.pointB.x + TABLETOP_DEFAULT_BEVEL_SIZE,
                        y: euroZapilPlank.localInterval.pointB.z -
                            (euroZapilPlank.localInterval.pointB.z === endPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    }
                ];
                for (point of points) {
                    if (!uniquePoints[point.x + '_' + point.y]) {
                        shapePoints.push(new Vector2(point.x, point.y))
                        uniquePoints[point.x + '_' + point.y] = true;
                    }
                }
            }
        }
    }

    protected addEuroZapilRightPoints(
        euroZapilPlanks: IEuroZapilPlank[] | undefined,
        startPoint: Vector2 | TPoint2D,
        endPoint: Vector2 | TPoint2D,
        shapePoints: Vector2[],
        uniquePoints: { [key: string]: boolean }
    ) {
        let euroZapilPlank: IEuroZapilPlank;
        let points: TPoint2D[];
        let point: TPoint2D;

        if (!euroZapilPlanks || MathHelper.isEqualPoints2D(startPoint, endPoint)) {
            return;
        }
        for (euroZapilPlank of euroZapilPlanks) {
            if (euroZapilPlank.localInterval.pointA.z <= startPoint.y &&
                euroZapilPlank.localInterval.pointB.z >= endPoint.y) {
                points = [
                    {
                        x: euroZapilPlank.localInterval.pointA.x - TABLETOP_DEFAULT_BEVEL_SIZE,
                        y: euroZapilPlank.localInterval.pointA.z -
                            (euroZapilPlank.localInterval.pointA.z === startPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    },
                    {
                        x: euroZapilPlank.localInterval.pointA.x - TABLETOP_DEFAULT_BEVEL_SIZE + this.service.getEuroZapilDepth(),
                        y: euroZapilPlank.localInterval.pointA.z - this.service.getEuroZapilDepth() -
                            (euroZapilPlank.localInterval.pointA.z === startPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    },
                    {
                        x: euroZapilPlank.localInterval.pointB.x - TABLETOP_DEFAULT_BEVEL_SIZE + this.service.getEuroZapilDepth(),
                        y: euroZapilPlank.localInterval.pointB.z +
                            (euroZapilPlank.localInterval.pointB.z === endPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    },
                    {
                        x: euroZapilPlank.localInterval.pointB.x - TABLETOP_DEFAULT_BEVEL_SIZE,
                        y: euroZapilPlank.localInterval.pointB.z +
                            (euroZapilPlank.localInterval.pointB.z === endPoint.y ? TABLETOP_DEFAULT_BEVEL_SIZE : 0)
                    }
                ];
                for (point of points) {
                    if (!uniquePoints[point.x + '_' + point.y]) {
                        shapePoints.push(new Vector2(point.x, point.y))
                        uniquePoints[point.x + '_' + point.y] = true;
                    }
                }
            }
        }
    }

    protected addEuroZapilFrontPoints(
        euroZapilPlanks: IEuroZapilPlank[] | undefined,
        startPoint: Vector2 | TPoint2D,
        endPoint: Vector2 | TPoint2D,
        shapePoints: Vector2[],
        uniquePoints: { [key: string]: boolean }
        ) {
        let euroZapilPlank: IEuroZapilPlank;
        let points: TPoint2D[] = [];
        let point: TPoint2D;

        if (!euroZapilPlanks || MathHelper.isEqualPoints2D(startPoint, endPoint)) {
            return;
        }
        for (euroZapilPlank of euroZapilPlanks) {
            switch (euroZapilPlank.neighborSide) {
                case SIDE_TYPE_LEFT:
                    points.push({
                        x: euroZapilPlank.localInterval.pointA.x +
                            (euroZapilPlank.localInterval.pointA.x === startPoint.x ? TABLETOP_DEFAULT_BEVEL_SIZE : 0),
                        y: euroZapilPlank.localInterval.pointA.z - TABLETOP_DEFAULT_BEVEL_SIZE
                    })
                    points.push({
                        x: euroZapilPlank.localInterval.pointA.x + this.service.getEuroZapilDepth() +
                            (euroZapilPlank.localInterval.pointA.x === startPoint.x ? TABLETOP_DEFAULT_BEVEL_SIZE : 0),
                        y: euroZapilPlank.localInterval.pointA.z - TABLETOP_DEFAULT_BEVEL_SIZE - this.service.getEuroZapilDepth()
                    })
                    points.push({
                        x: euroZapilPlank.localInterval.pointB.x -
                            (euroZapilPlank.localInterval.pointB.x === endPoint.x ? TABLETOP_DEFAULT_BEVEL_SIZE : 0),
                        y: euroZapilPlank.localInterval.pointB.z - TABLETOP_DEFAULT_BEVEL_SIZE - this.service.getEuroZapilDepth()
                    })
                    points.push({
                        x: euroZapilPlank.localInterval.pointB.x -
                            (euroZapilPlank.localInterval.pointB.x === endPoint.x ? TABLETOP_DEFAULT_BEVEL_SIZE : 0),
                        y: euroZapilPlank.localInterval.pointB.z - TABLETOP_DEFAULT_BEVEL_SIZE
                    })
                    break;
                case SIDE_TYPE_RIGHT:
                    points.push({
                        x: euroZapilPlank.localInterval.pointA.x +
                            (euroZapilPlank.localInterval.pointA.x === startPoint.x ? TABLETOP_DEFAULT_BEVEL_SIZE : 0),
                        y: euroZapilPlank.localInterval.pointA.z - TABLETOP_DEFAULT_BEVEL_SIZE
                    })
                    points.push({
                        x: euroZapilPlank.localInterval.pointA.x +
                            (euroZapilPlank.localInterval.pointA.x === startPoint.x ? TABLETOP_DEFAULT_BEVEL_SIZE : 0),
                        y: euroZapilPlank.localInterval.pointA.z - TABLETOP_DEFAULT_BEVEL_SIZE - this.service.getEuroZapilDepth()
                    })
                    points.push({
                        x: euroZapilPlank.localInterval.pointB.x - this.service.getEuroZapilDepth(),
                        y: euroZapilPlank.localInterval.pointB.z - TABLETOP_DEFAULT_BEVEL_SIZE - this.service.getEuroZapilDepth()
                    })
                    points.push({
                        x: euroZapilPlank.localInterval.pointB.x,
                        y: euroZapilPlank.localInterval.pointB.z - TABLETOP_DEFAULT_BEVEL_SIZE
                    })
                    break;
            }
        }
        for (point of points) {
            if (!uniquePoints[point.x + '_' + point.y]) {
                shapePoints.push(new Vector2(point.x, point.y))
                uniquePoints[point.x + '_' + point.y] = true;
            }
        }
    }

    protected getPointWithBevel(
        prevPoint: Vector2 | TPoint2D | undefined,
        point: Vector2 | TPoint2D,
        nextPoint: Vector2 | TPoint2D | undefined,
        sideType: TSideType
    ): TPoint2D {
        let prevLine: TLine;
        let line: TLine;
        let resultPoint: TPoint2D | undefined;

        if (!prevPoint) {
            prevPoint = new Vector2(
                sideType === SIDE_TYPE_LEFT ? 10000 : -10000,
                point.y
            );
        }
        if (!nextPoint) {
            nextPoint = new Vector2(
                sideType === SIDE_TYPE_LEFT ? 10000 : -10000,
                point.y
            );
        }
        prevLine = MathHelper.getParallelLinePoints(
            {x: prevPoint.x, y: prevPoint.y},
            {
                x: point.x,
                y: point.y,
            },
            TABLETOP_DEFAULT_BEVEL_SIZE
        );
        line = MathHelper.getParallelLinePoints(
            {x: point.x, y: point.y},
            {
                x: nextPoint.x,
                y: nextPoint.y,
            },
            TABLETOP_DEFAULT_BEVEL_SIZE
        );
        if (prevLine && line) {
            resultPoint = MathHelper.getIntersectionPoint(prevLine, line);
        }
        if (!resultPoint) {
            resultPoint = {
                x: point.x,
                y: point.y,
            };
        }

        if (resultPoint.x > this.getLength() / 2 - TABLETOP_DEFAULT_BEVEL_SIZE) {
            resultPoint.x = this.getLength() / 2 - TABLETOP_DEFAULT_BEVEL_SIZE;
        }
        if (resultPoint.x < -this.getLength() / 2 + TABLETOP_DEFAULT_BEVEL_SIZE) {
            resultPoint.x = -this.getLength() / 2 + TABLETOP_DEFAULT_BEVEL_SIZE;
        }
        if (resultPoint.y > this.getWidth() / 2 - TABLETOP_DEFAULT_BEVEL_SIZE) {
            resultPoint.y = this.getWidth() / 2 - TABLETOP_DEFAULT_BEVEL_SIZE;
        }
        if (resultPoint.y < -this.getWidth() / 2 + TABLETOP_DEFAULT_BEVEL_SIZE) {
            resultPoint.y = -this.getWidth() / 2 + TABLETOP_DEFAULT_BEVEL_SIZE;
        }

        return resultPoint;
    }

    public createBody() {
        this.body = new Mesh(this.getBodyGeometry(), this.getBodyMaterial());
        this.body.castShadow = true;
        this.body.position.y += this.getHeightShape()/4;
        this.body.rotation.x = 0.5 * Math.PI;
        this.body.updateMatrix();
        this.body.geometry.applyMatrix4(this.body.matrix);
        this.body.rotation.set(0, 0, 0);
        this.body.renderOrder = 2;
        this.body.userData.commonRenderOrder = 2;
        this.body.name = "tabletops";
        this.body.matrixAutoUpdate = false;
        this.body.userData.notTransparentForBack = true;
        this.body.updateMatrix();
        this.view3d.add(this.body);
        this.addCoverPoints(this.calculateMeshCoverPoints(this.body));
    }

    public getBodyMaterial(): MeshStandardMaterial {
        return new MeshStandardMaterial({
            color: this.materialData.color || "#ffffff",
            map: this.materialTextures.texture || null,
            normalMap: this.materialTextures.normal || null,
            roughnessMap: this.materialTextures.roughness || null,
            envMapIntensity: 5,
            name:"TabletopMaterial"
        });
    }

    public getDefaultRotationByType(): Euler {
        return new Euler();
    }

    protected getBodyGeometry(): BufferGeometry {
        let geometry: BufferGeometry;
        let extrudeSettings: ExtrudeGeometryOptions;
        let position: BufferAttribute | InterleavedBufferAttribute | GLBufferAttribute;
        let index: number;
        let length: number;
        let itemPositionX: number;
        let itemPositionY: number;
        let euroZapilPlanks: IEuroZapilPlanks;
        const sizes: TSizes = this.getSizes();

        extrudeSettings = {
            steps: 1,
            depth: this.getHeightShape(),
            bevelEnabled: true,
            bevelThickness: this.getRoundingBevel(),
            bevelSize: this.getRoundingBevel(),
            bevelSegments: TABLETOP_DEFAULT_BEVEL_SEGMENTS
        };
        geometry = new ExtrudeGeometry(this.shape, extrudeSettings);
        position = geometry.attributes.position;
        euroZapilPlanks = this.getEuroZapilPlanks();
        const isLeftFlange: boolean = !!(euroZapilPlanks[SIDE_TYPE_LEFT] && euroZapilPlanks[SIDE_TYPE_LEFT].length);
        const rightFlange: boolean = !!(euroZapilPlanks[SIDE_TYPE_RIGHT] && euroZapilPlanks[SIDE_TYPE_RIGHT].length);
        for (index = 0, length = position.count; index < length; index++) {
            if ('isGLBufferAttribute' in position) {
                continue;
            }
            itemPositionX = position.getX(index);
            itemPositionY = position.getY(index);
            if (itemPositionY <= (-sizes.width / 2 + this.getRoundingBevel())) {
                position.setY(index, -sizes.width / 2)
            }
            if (!isLeftFlange && itemPositionX <= (-sizes.length / 2 + this.getRoundingBevel())) {
                position.setX(index, -sizes.length / 2)
            }
            if (!rightFlange && itemPositionX >= (sizes.length / 2 - this.getRoundingBevel())) {
                position.setX(index, sizes.length / 2);
            }
        }

        return geometry;
    }

    protected getHeightShape(): number {
        return this.getHeight() - this.getRoundingBevel() * 2;
    }

    protected getRoundingBevel(): number {
        return this.materialData.roundingBevel !== undefined && !isNaN(+this.materialData.roundingBevel) ?
            this.materialData.roundingBevel : TABLETOP_DEFAULT_BEVEL_SIZE;
    }
}
